import { BookOpen, ClipboardCheck, Target, TrendingUp, Users, Star } from 'lucide-react';
import { RealScreen } from '@/components/marketing/roleKit';

/**
 * Students Real Screen 3: feedback. Fields mirror the real SessionFeedback view:
 * rating, engagement, topics covered, notes, homework, areas for improvement, progress summary.
 */
function Stars({ n }: { n: number }) {
  return (
    <span className="flex gap-0.5" aria-label={`${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} aria-hidden />
      ))}
    </span>
  );
}

function Note({ icon: Icon, title, body, tone }: { icon: typeof BookOpen; title: string; body: string; tone: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${tone}`}>
        <Icon className="h-3 w-3" aria-hidden />
      </span>
      <div>
        <p className="text-xs font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

export function FeedbackFeatureSection() {
  return (
    <RealScreen
      id="section-feedback"
      audience="students"
      layout="below"
      heading="Every session ends with notes"
      lede="Your tutor writes up what you covered and what to do next, so you know where you stand between sessions."
      callouts={[
        { icon: ClipboardCheck, title: 'Topics, homework, next focus', body: 'Structured notes, not a vague thumbs-up.' },
        { icon: Users, title: 'Linked parents can read it', body: 'If a parent is linked to your account, they can see the feedback too.' },
        { icon: BookOpen, title: 'Find it again', body: 'Past feedback stays with the session in your Sessions list.' },
      ]}
      caption="Example session feedback."
    >
      <div className="mx-auto max-w-3xl rounded-xl border bg-background p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Session feedback</p>
            <p className="text-xs text-muted-foreground">SAT Math · last Wednesday</p>
          </div>
          <span className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-medium text-white">Attended</span>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg bg-muted/50 px-3 py-2">
            <p className="text-xs text-muted-foreground">Session rating</p>
            <Stars n={5} />
          </div>
          <div className="rounded-lg bg-muted/50 px-3 py-2">
            <p className="text-xs text-muted-foreground">Engagement</p>
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Highly engaged</p>
          </div>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Note icon={BookOpen} tone="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" title="Topics covered" body="Linear equations in word problems, setting up before solving." />
          <Note icon={Target} tone="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title="Areas for improvement" body="Translating a sentence into an equation on the first read." />
          <Note icon={ClipboardCheck} tone="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" title="Homework" body="Ten word problems; write the equation before any arithmetic." />
          <Note icon={TrendingUp} tone="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" title="Progress" body="Faster at spotting which quantity the question is asking for." />
        </div>
      </div>
    </RealScreen>
  );
}
